/**
 * Responsibility: turn one radio telescope dish's parameters into the geometry that draws and masks it.
 *
 * ## The one curved surface in the world
 *
 * Everything else here is boxes, cones, ring stacks and lines. A parabolic bowl is the only genuinely curved
 * *surface*, and in wireframe a ring of concentric circles reads as a dish immediately — there is nothing else it
 * could be. That is the whole reason this shape is worth building.
 *
 * ## Still a ring stack, just pointed somewhere
 *
 * Concentric rings of increasing radius, offset along the bowl's axis by a squared depth. Because the rings are
 * threaded along an axis exactly as a rock column's are threaded up a vertical line, the shared ring-stack line
 * emitter and the shared depth-mask emitter both serve it — the axis simply is not vertical. The radial lines that
 * make it read as a dish rather than as a target come free: they are the longitudinals joining consecutive rings.
 *
 * The mask closes at the rim, which turns the bowl into a solid lens. That is right: a dish is sheet metal, so its
 * near half should hide its far half.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { emitRingStack, segmentsForRingStack, maskForRingStack, colorAtHeight } from './structureGeometry.js';
import { TELESCOPE } from '../config.js';

/** Segments the mount below the bowl occupies: a mast plus one leg per foot. */
const MOUNT_SEGMENTS = 1 + TELESCOPE.MOUNT_LEGS;

/** Cross product, for building the bowl's local frame. */
function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function normalize(v) {
  const length = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / length, v[1] / length, v[2] / length];
}

/**
 * Unit vector the dish points along, from its azimuth and elevation.
 *
 * @param {{azimuth: number, elevation: number}} dish
 * @returns {[number, number, number]}
 */
export function dishAxis(dish) {
  const horizontal = Math.cos(dish.elevation);
  return [horizontal * Math.sin(dish.azimuth), Math.sin(dish.elevation), horizontal * Math.cos(dish.azimuth)];
}

/**
 * Two unit vectors spanning the plane the rings lie in.
 *
 * The reference direction is swapped when the dish points near the zenith, because a cross product with a nearly
 * parallel vector collapses — which would produce a dish of zero width rather than an error anyone would notice.
 */
function ringFrame(axis) {
  const reference = Math.abs(axis[1]) > TELESCOPE.AXIS_SINGULARITY_GUARD ? [0, 0, 1] : [0, 1, 0];
  const right = normalize(cross(axis, reference));

  return [right, cross(right, axis)];
}

/** Where the bowl's vertex sits: the top of the mount. */
function vertexOf(dish) {
  return [dish.x, dish.groundY + dish.mountHeight, dish.z];
}

/**
 * The ring stack for a dish, ready for both line and mask emission.
 *
 * @param {object} dish
 * @param {number} dish.x
 * @param {number} dish.z
 * @param {number} dish.groundY - Ground under the mount.
 * @param {number} dish.mountHeight - Height of the bowl's vertex above the ground.
 * @param {number} dish.radius - Rim radius.
 * @param {number} dish.azimuth - Compass direction the bowl faces, radians.
 * @param {number} dish.elevation - Angle above the horizon the bowl faces, radians.
 * @param {number} dish.rings - Concentric rings, innermost to rim.
 * @param {number} dish.sides - Points per ring.
 * @returns {{rings: Array<{points: Array<[number, number, number]>}>}}
 */
export function dishSolid(dish) {
  const axis = dishAxis(dish);
  const [right, up] = ringFrame(axis);
  const vertex = vertexOf(dish);

  /** Depth of the bowl at the rim. The profile below is this scaled by the squared radius fraction. */
  const rimDepth = dish.radius * TELESCOPE.DISH_DEPTH_RATIO;

  const rings = [];

  for (let i = 0; i < dish.rings; i += 1) {
    /*
      Innermost ring is one step out rather than at the vertex itself. A ring of zero radius would collapse into a
      point and emit a fan of zero-length segments — the same reason the spire profile keeps a small tip.
    */
    const fraction = (i + 1) / dish.rings;
    const radius = dish.radius * fraction;
    const depth = rimDepth * fraction * fraction;

    const points = [];
    for (let k = 0; k < dish.sides; k += 1) {
      const angle = ((k + (dish.spin ?? 0)) / dish.sides) * Math.PI * 2;
      const across = Math.cos(angle) * radius;
      const along = Math.sin(angle) * radius;

      points.push([
        vertex[0] + axis[0] * depth + right[0] * across + up[0] * along,
        vertex[1] + axis[1] * depth + right[1] * across + up[1] * along,
        vertex[2] + axis[2] * depth + right[2] * across + up[2] * along,
      ]);
    }

    rings.push({ points });
  }

  return { rings };
}

/** Segments one dish occupies: the bowl's ring stack plus its mount. */
export function segmentsForDish(dish) {
  return segmentsForRingStack(dish.rings, dish.sides) + MOUNT_SEGMENTS;
}

/** Mask vertices and triangles one dish occupies. The mount casts none — it is an open frame. */
export function maskForDish(dish) {
  return maskForRingStack(dish.rings, dish.sides);
}

/**
 * Highest point the dish reaches above its ground, for colour normalisation.
 *
 * Depends on where it is pointing: a bowl aimed at the zenith stands taller than one aimed at the horizon. Derived
 * rather than stored, so a re-aimed dish cannot end up with a stale extent.
 *
 * @param {object} dish
 * @returns {number}
 */
export function dishHeight(dish) {
  const highest = dishSolid(dish).rings.at(-1).points.reduce((peak, [, y]) => Math.max(peak, y), 0);
  return Math.max(1, highest - dish.groundY);
}

/**
 * Emit one dish: the bowl, then the mount holding it up.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} dish - Same shape `dishSolid` takes.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitDish(writer, dish, gradient) {
  const extent = { groundY: dish.groundY, height: dishHeight(dish) };

  let written = emitRingStack(writer, dishSolid(dish), gradient, extent);

  const vertex = vertexOf(dish);
  const colourAt = (y) => colorAtHeight(gradient, (y - extent.groundY) / extent.height);
  const footRadius = dish.radius * TELESCOPE.MOUNT_FOOT_RATIO;

  // Mast, straight up to the bowl's vertex.
  writer.write(dish.x, dish.groundY, dish.z, vertex[0], vertex[1], vertex[2], colourAt(dish.groundY), colourAt(vertex[1]));
  written += 1;

  /*
    Splayed legs from a small footprint up to the same point. Open frame, so unlike the bowl it casts no depth mask —
    the same call the lattice tower and the windpump make.
  */
  for (let leg = 0; leg < TELESCOPE.MOUNT_LEGS; leg += 1) {
    const angle = (leg / TELESCOPE.MOUNT_LEGS) * Math.PI * 2;
    const footX = dish.x + Math.cos(angle) * footRadius;
    const footZ = dish.z + Math.sin(angle) * footRadius;

    writer.write(footX, dish.groundY, footZ, vertex[0], vertex[1], vertex[2], colourAt(dish.groundY), colourAt(vertex[1]));
    written += 1;
  }

  return written;
}
